import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Award, Users, Globe, Target, Brain, Lock, Zap, CheckCircle, ArrowRight, Clock, BarChart as ChartBar } from 'lucide-react';

interface WhyChooseUsProps {
  setCurrentPage?: (page: string) => void;
}

const WhyChooseUs: React.FC<WhyChooseUsProps> = ({ setCurrentPage }) => {
  const reasons = [
    {
      icon: Shield,
      title: "Industry Leadership",
      description: "With over a decade of experience in cybersecurity, we've established ourselves as a trusted leader in providing comprehensive security solutions."
    },
    {
      icon: Brain,
      title: "Innovative Solutions",
      description: "Our cutting-edge technology and advanced methodologies ensure your organization stays ahead of evolving cyber threats."
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Our certified security professionals bring deep expertise and industry knowledge to protect your digital assets."
    },
    {
      icon: Globe,
      title: "Global Presence",
      description: "Operating worldwide with local expertise to deliver consistent security services across multiple regions."
    }
  ];

  const capabilities = [
    {
      icon: Lock,
      title: "Comprehensive Security",
      items: [
        "Advanced threat detection and prevention",
        "24/7 security monitoring",
        "Incident response and recovery",
        "Security assessment and testing"
      ]
    },
    {
      icon: Target,
      title: "Strategic Approach",
      items: [
        "Customized security solutions",
        "Risk-based methodology",
        "Compliance-driven framework",
        "Continuous improvement"
      ]
    },
    {
      icon: Clock,
      title: "Reliable Support",
      items: [
        "Round-the-clock assistance",
        "Dedicated security team",
        "Rapid response times",
        "Proactive monitoring"
      ]
    }
  ];

  const differentiators = [
    {
      icon: ChartBar,
      title: "Proven Track Record",
      description: "Successfully protecting organizations worldwide",
      stats: [
        { value: "99.9%", label: "Security Uptime" },
        { value: "15min", label: "Avg. Response Time" },
        { value: "24/7", label: "Monitoring" }
      ]
    },
    {
      icon: Brain,
      title: "Advanced Technology",
      description: "Leveraging AI and machine learning for enhanced security",
      stats: [
        { value: "100+", label: "Security Tools" },
        { value: "5M+", label: "Threats Blocked" },
        { value: "Real-time", label: "Analysis" }
      ]
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Certified professionals with extensive industry experience",
      stats: [
        { value: "200+", label: "Security Experts" },
        { value: "50+", label: "Certifications" },
        { value: "15+ Years", label: "Experience" }
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Award className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Why Choose AstronShield?</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Experience unmatched cybersecurity expertise and innovative solutions that protect your organization's digital future
          </motion.p>
        </div>

        {/* Key Reasons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20"
        >
          {reasons.map((reason, index) => (
            <div key={index} className="glass-panel rounded-xl p-6 hover-card">
              <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg mb-6">
                <reason.icon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{reason.title}</h3>
              <p className="text-muted">{reason.description}</p>
            </div>
          ))}
        </motion.div>

        {/* Core Capabilities */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Core Capabilities</h2>
            <p className="text-lg text-muted">Comprehensive security solutions for your organization</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <capability.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold ml-4">{capability.title}</h3>
                </div>
                <ul className="space-y-4">
                  {capability.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Key Differentiators */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">What Sets Us Apart</h2>
            <p className="text-lg text-muted">Industry-leading expertise and proven results</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {differentiators.map((diff, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <diff.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold ml-4">{diff.title}</h3>
                </div>
                <p className="text-muted mb-6">{diff.description}</p>
                <div className="grid grid-cols-3 gap-4">
                  {diff.stats.map((stat, statIndex) => (
                    <div key={statIndex} className="text-center">
                      <div className="text-xl font-bold text-gradient mb-1">{stat.value}</div>
                      <div className="text-sm text-muted">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Experience the Difference?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Join leading organizations worldwide who trust AstronShield for their cybersecurity needs
            </p>
            <button 
              onClick={() => setCurrentPage && setCurrentPage('contact')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WhyChooseUs;